/** Cash Signals' own body: the customers table with a signal per customer,
 *  the card for the customer picked in it, and the cheque lines. Every word
 *  comes from the server (cash_engine.customers_table, cheque_lines); this
 *  only lays it out and keeps which tab and which row is open. */
import { useMemo, useState } from 'react';

import {
  CashCustomer,
  CashCustomers,
  ChequeLines,
  CustomerSignal
} from '@/components/engine/api';
import { surface, useAsk } from '@/components/know-your-data/blocks';

const SIGNAL_STYLE: Record<CustomerSignal['kind'], React.CSSProperties> = {
  stopped: {
    background: 'rgba(220, 38, 38, 0.10)',
    color: 'var(--sig-negative)'
  },
  growing: { background: 'var(--sig-warn-bg)', color: 'var(--sig-warn-text)' },
  low_rate: { background: 'var(--sig-warn-bg)', color: 'var(--sig-warn-text)' },
  paying_down: { background: 'var(--sig-chip)', color: 'var(--sig-blue-dark)' },
  steady: {
    background: 'rgba(5, 150, 105, 0.10)',
    color: 'var(--sig-positive)'
  }
};

const TAB_KIND: Record<string, CustomerSignal['kind'] | null> = {
  all: null,
  stopped: 'stopped',
  growing: 'growing',
  low_rate: 'low_rate'
};

export function SignalChip({ signal }: { signal: CustomerSignal }) {
  return (
    <span
      className="inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-[11.5px] font-semibold"
      style={SIGNAL_STYLE[signal.kind]}
    >
      {signal.label}
    </span>
  );
}

function Initial({
  name,
  kind
}: {
  name: string;
  kind: CustomerSignal['kind'];
}) {
  const letter = (name.trim().match(/\p{L}/u) ?? ['?'])[0].toUpperCase();
  return (
    <span
      className="grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
      style={SIGNAL_STYLE[kind]}
    >
      {letter}
    </span>
  );
}

export function CustomersTable({
  customers,
  selected,
  onSelect
}: {
  customers: CashCustomers;
  selected: CashCustomer | null;
  onSelect: (c: CashCustomer) => void;
}) {
  const [tab, setTab] = useState(customers.tabs[0]?.id ?? 'all');
  const rows = useMemo(() => {
    const kind = TAB_KIND[tab] ?? null;
    return kind
      ? customers.rows.filter((r) => r.signal.kind === kind)
      : customers.rows;
  }, [customers, tab]);
  const { ask } = useAsk();
  return (
    <section className="min-w-0 rounded-xl border" style={surface}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3.5">
        <h2 className="me-2 text-[15px] font-bold text-[var(--sig-ink)]">
          Customers
        </h2>
        {customers.tabs.map((t) => {
          const on = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className="h-8 rounded-full border px-3 text-[13px] font-semibold transition-colors"
              style={
                on
                  ? {
                      background: 'var(--sig-ink)',
                      borderColor: 'var(--sig-ink)',
                      color: 'var(--sig-surface)'
                    }
                  : { color: 'var(--sig-body)' }
              }
            >
              {t.label} · {t.count}
            </button>
          );
        })}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr
              className="text-[11.5px] uppercase tracking-wide text-muted-foreground"
              style={{ background: 'var(--sig-table-head)' }}
            >
              <th className="px-4 py-2.5 text-start font-semibold">Customer</th>
              <th className="px-4 py-2.5 text-end font-semibold">Owed</th>
              <th className="px-4 py-2.5 text-end font-semibold">
                3 months ago
              </th>
              <th className="px-4 py-2.5 text-start font-semibold">
                Last payment
              </th>
              <th className="px-4 py-2.5 text-start font-semibold">Signal</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const on = selected?.name === r.name;
              return (
                <tr
                  key={r.name}
                  className="cursor-pointer border-t hover:bg-[var(--sig-chip)]"
                  style={{
                    borderColor: 'var(--sig-border-row)',
                    background: on ? 'var(--sig-tint)' : undefined
                  }}
                  onClick={() => onSelect(r)}
                >
                  <td className="px-4 py-2.5 text-[var(--sig-ink)]">
                    <span className="flex items-center gap-2.5">
                      <Initial name={r.name} kind={r.signal.kind} />
                      <bdi className="min-w-0">{r.name}</bdi>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-end font-bold tabular-nums text-[var(--sig-ink)]">
                    <span dir="ltr">{r.owed_display}</span>
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums text-[var(--sig-body)]">
                    <span dir="ltr">{r.ago_display ?? '–'}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-[var(--sig-body)]">
                    {r.last_payment}
                  </td>
                  <td className="px-4 py-2.5">
                    <SignalChip signal={r.signal} />
                  </td>
                  <td className="px-4 py-2.5 text-end">
                    <button
                      type="button"
                      className="whitespace-nowrap text-[13px] font-semibold text-primary hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        ask(r.asks[0].question);
                      }}
                    >
                      Ask →
                    </button>
                  </td>
                </tr>
              );
            })}
            {!rows.length ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-muted-foreground"
                >
                  Nobody on this list.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-3 text-xs text-muted-foreground">
        {customers.note}
      </p>
    </section>
  );
}

export function CustomerCard({ customer }: { customer: CashCustomer }) {
  const { ask } = useAsk();
  const c = customer;
  const now = c.owed ?? 0;
  const ago = c.ago ?? 0;
  const max = Math.max(now, ago, 1);
  const pct = (v: number) => `${Math.max(6, Math.round((v / max) * 100))}%`;
  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-xl border p-4"
      style={surface}
    >
      <SignalChip signal={c.signal} />
      <h2 className="text-[17px] font-bold leading-snug text-[var(--sig-ink)]">
        <bdi>{c.name}</bdi>
      </h2>
      <div className="grid grid-cols-2 gap-2.5">
        <div
          className="rounded-lg px-3 py-2.5"
          style={{ background: 'var(--sig-chip)' }}
        >
          <div className="text-[11.5px] text-muted-foreground">Owed today</div>
          <div
            className="text-xl font-bold tabular-nums text-[var(--sig-ink)]"
            dir="ltr"
          >
            {c.owed_display}
          </div>
        </div>
        <div
          className="rounded-lg px-3 py-2.5"
          style={{ background: 'var(--sig-chip)' }}
        >
          <div className="text-[11.5px] text-muted-foreground">
            3 months ago
          </div>
          <div
            className="text-xl font-bold tabular-nums text-[var(--sig-ink)]"
            dir="ltr"
          >
            {c.ago_display ?? '–'}
          </div>
        </div>
      </div>
      {c.ago !== null ? (
        <div>
          <div className="mb-1.5 text-xs text-muted-foreground">
            Balance, then and now
          </div>
          <div className="grid h-20 grid-cols-2 items-end gap-3">
            {[
              { v: ago, l: '3 months ago', tone: 'var(--sig-border-blue)' },
              { v: now, l: 'Today', tone: 'var(--sig-blue)' }
            ].map((b) => (
              <div key={b.l} className="flex h-full flex-col justify-end gap-1">
                <div
                  className="rounded-t-md"
                  style={{ height: pct(b.v), background: b.tone }}
                />
                <span className="text-center text-[11px] text-muted-foreground">
                  {b.l}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <p className="text-[13px] text-[var(--sig-body)]">
        Last invoice {c.last_invoice} · last payment {c.last_payment}.
      </p>
      <div className="flex flex-wrap gap-2">
        {c.asks.map((a, i) => (
          <button
            key={a.label}
            type="button"
            onClick={() => ask(a.question)}
            className="h-9 rounded-lg px-3.5 text-[13px] font-semibold"
            style={
              i === 0
                ? { background: 'var(--sig-blue-deep)', color: '#fff' }
                : {
                    border: '1px solid var(--sig-border-strong)',
                    color: 'var(--sig-body)'
                  }
            }
          >
            {a.label}
          </button>
        ))}
      </div>
    </section>
  );
}

const LINE_STYLE: Record<
  ChequeLines['lines'][number]['kind'],
  React.CSSProperties
> = {
  ok: SIGNAL_STYLE.steady,
  warn: SIGNAL_STYLE.growing,
  out: SIGNAL_STYLE.paying_down,
  bad: SIGNAL_STYLE.stopped
};

export function ChequesCard({ cheques }: { cheques: ChequeLines }) {
  return (
    <section className="min-w-0 rounded-xl border" style={surface}>
      <div className="flex items-center gap-2 px-4 pb-1.5 pt-3.5">
        <h2 className="text-[15px] font-bold text-[var(--sig-ink)]">Cheques</h2>
        <span className="text-xs text-muted-foreground">{cheques.as_of}</span>
      </div>
      {cheques.lines.map((l) => (
        <div
          key={l.label}
          className="flex items-center gap-3 border-t px-4 py-2.5"
          style={{ borderColor: 'var(--sig-border-row)' }}
        >
          <span
            className="inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-[11.5px] font-semibold"
            style={LINE_STYLE[l.kind]}
          >
            {l.label}
          </span>
          <span className="text-[13.5px] text-[var(--sig-body)]">{l.text}</span>
          <span
            className="ms-auto whitespace-nowrap font-bold tabular-nums text-[var(--sig-ink)]"
            dir="ltr"
          >
            {l.value}
          </span>
        </div>
      ))}
    </section>
  );
}
